import { useMemo } from "react";
import { useVehicles } from "./useVehicles";
import { useTrips } from "./useTrips";
import { useExpenses } from "./useExpenses";

export interface SetupStep {
  key: string;
  label: string;
  completed: boolean;
  href: string;
}

export const useSetupProgress = () => {
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();
  const { trips, isLoading: tripsLoading } = useTrips();
  const { expenses, isLoading: expensesLoading } = useExpenses();

  const steps = useMemo<SetupStep[]>(() => {
    const hasVehicle = (vehicles?.length || 0) > 0;
    const hasTrip = (trips?.length || 0) > 0;
    const hasExpense = (expenses?.length || 0) > 0;

    // Check if any expense is assigned to a vehicle
    const hasAssignedExpense = expenses?.some((e) => e.vehicle_id) || false;

    // "First profit snapshot" = has at least one vehicle with both a trip and an expense
    const vehicleIds = new Set(vehicles?.map((v) => v.id) || []);
    const vehiclesWithTrips = new Set(
      trips?.filter((t) => t.vehicle_id && vehicleIds.has(t.vehicle_id)).map((t) => t.vehicle_id) || []
    );
    const vehiclesWithExpenses = new Set(
      expenses?.filter((e) => e.vehicle_id && vehicleIds.has(e.vehicle_id)).map((e) => e.vehicle_id) || []
    );
    const hasFirstSnapshot = [...vehiclesWithTrips].some((id) => vehiclesWithExpenses.has(id!));

    return [
      { key: "vehicle", label: "Add your first vehicle", completed: hasVehicle, href: "/app/vehicles" },
      { key: "trip", label: "Enter earnings for at least one trip", completed: hasTrip, href: "/app/earnings" },
      { key: "expense", label: "Enter at least one expense", completed: hasExpense, href: "/app/expenses" },
      { key: "assign", label: "Assign an expense to a vehicle", completed: hasAssignedExpense, href: "/app/expenses" },
      { key: "snapshot", label: "Complete your first profit snapshot", completed: hasFirstSnapshot, href: "/app/orientation/signals" },
    ];
  }, [vehicles, trips, expenses]);

  const completedCount = steps.filter((s) => s.completed).length;
  const percentComplete = Math.round((completedCount / steps.length) * 100);
  const allComplete = completedCount === steps.length;

  return {
    steps,
    completedCount,
    percentComplete,
    allComplete,
    isLoading: vehiclesLoading || tripsLoading || expensesLoading,
  };
};
